/** The pieces of People and a person that only the Season layouts draw: the "Who to show" choices and two small sheets. */
import { useId, useState } from "react";
import { Link, useNavigate } from "react-router";
import type { PeopleFilters, PeopleRail, RoomStage } from "../../../shared/types";
import { GroupedList } from "../../components/season/GroupedList";
import { GroupHead } from "../../components/season/PageHead";
import { Picture } from "../../components/season/Picture";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { Chip } from "../../components/ui/Pill";
import { Sheet } from "../../components/ui/Sheet";
import { fmt } from "../../lib/when";
import { GROUP_LABEL, STAGE_NAME } from "./list";

const groupPath = (group: string) => (group === "all" ? "/people" : `/people?group=${encodeURIComponent(group)}`);

/** One choice in the list: a link, its name and how many. */
function Choice({
  to,
  on,
  title,
  value,
  bar,
  onPick,
}: {
  to: string;
  on: boolean;
  title: string;
  value: string;
  bar?: number;
  onPick?: () => void;
}) {
  return (
    <li className={on ? "k-lr k-on" : "k-lr"}>
      <Link className="k-lr-b" to={to} aria-current={on ? "page" : undefined} onClick={onPick}>
        <span className="k-lr-t">
          <b>{title}</b>
          {bar === undefined ? null : (
            <i className="pp-bar">
              <u style={{ width: `${bar}%` }} />
            </i>
          )}
        </span>
        <span className="k-lr-v">{value}</span>
      </Link>
    </li>
  );
}

/** The "Who to show" choices: the groups, the tags and each room with its stages. A sheet holds them on a phone, a pane on an iPad. */
export function WhoToShow({ rail, filters, onPick }: { rail: PeopleRail; filters: PeopleFilters; onPick?: () => void }) {
  const navigate = useNavigate();
  const [openRoom, setOpenRoom] = useState(filters.room ?? "");
  const on = filters.room ? "" : (filters.group ?? "all");
  const range = filters.range ? `&range=${filters.range}` : "";
  return (
    <div className="pp-rail">
      <GroupHead title="People" />
      <GroupedList noLead>
        {rail.groups.map((g) => (
          <Choice key={g.key} to={groupPath(g.key)} on={on === g.key} title={GROUP_LABEL[g.key]} value={fmt(g.count)} onPick={onPick} />
        ))}
      </GroupedList>
      {rail.tags.length ? (
        <>
          <GroupHead title="Tags" />
          <div className="k-chips">
            {rail.tags.map((t) => (
              <Chip
                key={t.tag}
                on={on === `tag:${t.tag}`}
                count={fmt(t.count)}
                onClick={() => {
                  navigate(groupPath(`tag:${t.tag}`));
                  onPick?.();
                }}
              >
                {t.tag}
              </Chip>
            ))}
          </div>
        </>
      ) : null}
      {rail.rooms.length ? (
        <>
          <GroupHead title="By room" />
          <GroupedList noLead className="pp-rooms">
            {rail.rooms.map((r) => {
              const open = openRoom === r.id;
              const reg = Math.max(1, r.stages.registered);
              const href = (st: RoomStage) => `/people?room=${encodeURIComponent(r.id)}&stage=${st}${range}`;
              const here = (st: RoomStage) => filters.room === r.id && (filters.stage ?? "registered") === st;
              const stage = (st: RoomStage) => (
                <Choice
                  key={st}
                  to={href(st)}
                  on={here(st)}
                  title={STAGE_NAME[st]}
                  value={fmt(r.stages[st])}
                  bar={Math.max(2, Math.round((r.stages[st] / reg) * 100))}
                  onPick={onPick}
                />
              );
              return (
                <li key={r.id} className="pp-room">
                  <button type="button" className="k-lr-b" aria-expanded={open} onClick={() => setOpenRoom(open ? "" : r.id)}>
                    <Picture src={r.poster_url} size="sm" />
                    <span className="k-lr-t">
                      <b dir="auto">{r.title}</b>
                      <span>{`${r.kind} · ${fmt(r.stages.registered)} registered`}</span>
                    </span>
                    <Icon name={open ? "down" : "chev"} />
                  </button>
                  {open ? (
                    <ul className="pp-stages">
                      {stage("registered")}
                      {stage("came")}
                      {stage("reached")}
                      {stage("clicked")}
                      <li className={here("nosign") ? "k-lr k-on" : "k-lr"}>
                        <Link className="k-lr-b pp-sub" to={href("nosign")} aria-current={here("nosign") ? "page" : undefined} onClick={onPick}>
                          <span className="k-lr-t">
                            <span>{`${fmt(r.stages.nosign)} of them not customers yet`}</span>
                          </span>
                          <Icon name="chev" />
                        </Link>
                      </li>
                      {stage("signed")}
                      {stage("missed")}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </GroupedList>
        </>
      ) : null}
    </div>
  );
}

/** "Add a tag for Priya": one field and, when People has been open, the business's tags to pick from. */
export function AddTagSheet({
  open,
  first,
  suggest,
  onAdd,
  onClose,
}: {
  open: boolean;
  first: string;
  /** Tags the business already uses and this person lacks. */
  suggest: readonly string[];
  /** Saves it; true when it saved. */
  onAdd: (tag: string) => Promise<boolean>;
  onClose: () => void;
}) {
  const id = useId();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    if (await onAdd(value)) {
      setValue("");
      onClose();
    }
    setBusy(false);
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Add a tag for ${first}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="plus" onClick={save} disabled={busy}>
            Add tag
          </Button>
        </>
      }
    >
      <div className="k-form">
        <div className="field">
          <label htmlFor={`${id}-t`}>New tag</label>
          <input dir="auto"
            id={`${id}-t`}
            className="inp"
            placeholder="Type a tag"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void save();
            }}
          />
        </div>
        {suggest.length ? (
          <div className="k-chips">
            {suggest.map((t) => (
              <Chip key={t} onClick={() => setValue(t)}>
                {t}
              </Chip>
            ))}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

/** "Note about Priya": your own note on a person. */
export function AddNoteSheet({ open, first, onSave, onClose }: { open: boolean; first: string; onSave: (text: string) => Promise<boolean>; onClose: () => void }) {
  const id = useId();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    if (await onSave(text)) {
      setText("");
      onClose();
    }
    setBusy(false);
  };
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Note about ${first}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" onClick={save} disabled={busy || !text.trim()}>
            Save note
          </Button>
        </>
      }
    >
      <div className="field">
        <label htmlFor={`${id}-n`}>Note</label>
        <textarea dir="auto" id={`${id}-n`} className="inp" rows={5} placeholder="Add a note" value={text} onChange={(e) => setText(e.target.value)} />
      </div>
    </Sheet>
  );
}
